/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState } from 'react';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import type { Props as AddEditInitialiseStaffModalProps, DataToEdit } from './modules/AddEditInitialiseStaffModal';
import type { Props as StaffLoginInfoModalProps } from './modules/StaffLoginInfoModal';
import type { Props as DeleteStaffModalProps } from './modules/DeleteStaffModal';
import { useI18n } from '@whizz/react-i18n';
import { AddEditInitialiseStaffModal, DeleteStaffModal, StaffLoginInfoModal, StaffTable } from './modules';
import { useModal } from '../../../../hooks/useModal';
import RosterManagementLayout from '../../../../layouts/RosterManagementLayout';
import { useQuery } from 'react-query';
import type { StaffGet, StaffPost } from '@whizz/api-model';
import { QUERIES_IDS } from '../../../../util/vars';
import { repository } from '@whizz/api-client';
import { useSuccessErrorModal } from '../../../../hooks/useSuccessErrorModal';
import { NewButton } from '../../../../global';
import { useIdentity } from '@whizz/react-identity';
import Highlighter from '../../../../components/highlighter/Highlighter';
import { useCleverIntegration } from '../../../../hooks/useCleverIntegration';

export const staffEmptyData: StaffGet = {
  classes: [],
  teachers: [],
  teachersClasses: [],
};

const Staff = (): JSX.Element => {
  const { translate } = useI18n();
  const { me } = useIdentity();
  const [ isClever ] = useCleverIntegration();
  const userCountry = (me as Teacher).country;
  const canAddOrEditStaff = userCountry ? ![ 'nz' ].includes(userCountry) : false; // Temp solution to assert if managed by iLearn (NZ)
  const [ newStaff, setNewStaff ] = useState<StaffPost['teacherId'] | null>(null);
  const [ highlightNewStaff, setHighlightNewStaff ] = useState<string | null>(null);
  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();

  const [ addEditStaffModal, toggleAddEditStaffModal, setAddEditStaffModalProps ] = useModal((props: AddEditInitialiseStaffModalProps) => (
    <AddEditInitialiseStaffModal {...props} />
  ));

  const [ staffLoginInfoModal, toggleStaffLoginInfoModal, setStaffLoginInfoModalProps ] = useModal((props: StaffLoginInfoModalProps) => (
    <StaffLoginInfoModal {...props} />
  ));

  const [ deleteModal, toggleDeleteModal, setDeleteModalProps ] = useModal((props: DeleteStaffModalProps) => (
    <DeleteStaffModal {...props} />
  ));

  const onSuccess = () => toggleMessageType('success');
  const onError = () => toggleMessageType('error');

  const handleAdd = () => {
    setAddEditStaffModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.ADD_TITLE'),
      mode: 'add',
      onSuccess: onCreate,
      onError,
      disableMaskClick: true,
    });

    toggleAddEditStaffModal(true);
  };

  const onCreate = (data: StaffPost) => {
    setHighlightNewStaff(null);
    setNewStaff(null);

    setStaffLoginInfoModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.STAFF.LOGIN_INFO_MODAL.TITLE'),
      data,
      disableMaskClick: true,
      closeCallback: () => {
        setHighlightNewStaff(null);

        setTimeout(() => {
          setHighlightNewStaff('tbody tr[data-new-entry=true]');
        }, 0);
      },
    });

    setTimeout(() => {
      setNewStaff(data.teacherId);
      toggleStaffLoginInfoModal(true);
    }, 0);
  };

  const handleEdit = (data: DataToEdit) => {
    setAddEditStaffModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.EDIT_TITLE'),
      mode: 'edit',
      toggleConfirm: toggleDeleteModal,
      onSuccess,
      onError,
      data,
      disableMaskClick: true,
    });

    setDeleteModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.STAFF.ADD_EDIT.DELETE_TITLE'),
      toggle: toggleAddEditStaffModal,
      toggleConfirm: toggleDeleteModal,
      onSuccess,
      onError,
      data,
      disableMaskClick: true,
    });

    toggleAddEditStaffModal(true);
  };

  const staffQuery = useQuery(
    [ QUERIES_IDS.STAFF ],
    () => repository.StaffRepository.getStaff(),
    {
      enabled: true,
      refetchOnMount: 'always',
      placeholderData: staffEmptyData,
    },
  );

  return (
    <RosterManagementLayout
      disableSection
    >
      {addEditStaffModal}
      {staffLoginInfoModal}
      {deleteModal}
      {successErrorModal}
      { canAddOrEditStaff ? (
        <div className={'flex justify-end mb-6'}>
          <NewButton
            disabled={isClever}
            onClick={handleAdd}
            data-cy={'rm-new-teacher'}
          >
            {translate('pages.ROSTER_MANAGEMENT.STAFF.BUTTONS.ADD')}
          </NewButton>
        </div>) : null }
      <StaffTable
        onEdit={canAddOrEditStaff && !isClever ? handleEdit : null}
        newStaff={newStaff}
        staffQuery={staffQuery}
      />
      <Highlighter
        query={highlightNewStaff}
        message={translate('pages.ROSTER_MANAGEMENT.STAFF.LOGIN_INFO_MODAL.CONTENT.LINE_1')}
        padding={0}
      />
    </RosterManagementLayout>
  );
};

export default Staff;
